<template>
    <div>
        <el-alert
            type="warning"
            show-icon
            :closable="false"
            class="mb-3"
        >
            <span v-if="ringGroup.force_agent_visibility_limits" class="text-blackish">
                With this setting turned on, agents will only see contacts and communications that they have access to based on their individual visibility settings 
                (e.g., "Owned Only" agents will only see their own contacts). This creates isolated views within the Inbox. For call routing, agents can only receive 
                and handle calls for contacts they can see - if an agent has "Owned Only" visibility, they won't receive calls for contacts owned by other agents. 
                Supervisors and Admins will always see all contacts and communications regardless of this setting.
            </span>
            <span v-else class="text-blackish">
                With this setting turned off, all agents will be able to see all contacts and communications within this Inbox, regardless of their individual visibility settings. 
                This is the default behavior where agents have full access to the Inbox. For call routing, incoming calls will be distributed to all available agents in the 
                inbox as usual, and any agent can handle any conversation.
            </span>
        </el-alert>
        <el-form-item>
            <div class="form-label mb-3 d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Agents Visibility Preview</h5>
                <el-tooltip content="Refresh the list of agents">
                    <el-button 
                        size="small"
                        :loading="loading_users" 
                        @click="refresh"
                    >
                        <i v-show="!loading_users" class="fa fa-refresh mr-1"></i>
                        Refresh
                    </el-button>
                </el-tooltip>
            </div>
            <el-table
                :data="tableData"
            >
                <el-table-column label="Agent Name" prop="name" sortable />
                <el-table-column label="Contacts Visibility" prop="contacts_visibility" />
                <el-table-column label="Communications Visibility" prop="communications_visibility" />
                <el-table-column
                    fixed="right"
                    label="Operations"
                    width="150"
                    align="center"
                >
                    <template v-slot="scope">
                        <div v-if="hasPermissionTo('update user')">
                            <router-link
                                :to="{ name: 'User Dialog', params: { user_id: scope.row.id }}">
                                <button class="btn btn-sm btn-block greenish"
                                        data-testid="settings-button">
                                    <i class="fa fa-cogs pull-left"></i>
                                    Settings
                                </button>
                            </router-link>
                        </div>
                    </template>
                </el-table-column>
            </el-table>
        </el-form-item>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import axios from 'axios'
import { COMPANY_AGENT } from '../../constants/roles'
import { user_mixin } from '../../mixins'
import * as ContactAccessTypes from '../../constants/contact-access-types'
import * as CommunicationAccessTypes from '../../constants/communication-access-types'

export default {
    name: 'RingGroupUsersVisibilityPreview',

    props: {
        ringGroup: {
            type: Object,
            required: true
        }
    },

    mixins: [user_mixin],

    data() {
        return {
            loading_users: false
        }
    },

    computed: {
        ...mapState(['users']),

        allRingGroupUsers() {
            const userIds = this.ringGroup.user_ids
            const watcherUsersIds = this.ringGroup.watcher_user_ids
            const teamUsersIds = this.ringGroup.teams.map(team => team.users.map(user => user.id)).flat()
            const watcherTeamUsersIds = this.ringGroup.watcher_team_user_ids?.flat()

            return [
                ...userIds,
                ...watcherUsersIds,
                ...teamUsersIds,
                ...watcherTeamUsersIds
            ]
        },

        tableData() {
            return this
                .users
                .filter(user => this.allRingGroupUsers.includes(user.id))
                .filter(user => user.role_names.includes(COMPANY_AGENT))
                .map(user => ({
                    id: user.id,
                    name: user.name,
                    contacts_visibility: this.ringGroup.force_agent_visibility_limits
                        ? this.formatContactsVisibility(user.contacts_visibility)
                        : 'Everything',
                    communications_visibility: this.ringGroup.force_agent_visibility_limits
                        ? this.formatCommunicationsVisibility(user.communications_visibility)
                        : 'Everything'
                }))
                .sort((a, b) => a.name.localeCompare(b.name))
        }
    },

    methods: {
        ...mapActions(['setUsers']),

        formatContactsVisibility(visibility) {
            const mapping = {
                [ContactAccessTypes.CONTACTS_ACCESS_EVERYONE]: 'Everything',
                [ContactAccessTypes.CONTACTS_ACCESS_RING_GROUP_USERS]: 'Team Owned Only',
                [ContactAccessTypes.CONTACTS_ACCESS_OWNED_ONLY]: 'Owned Only'
            }
            return mapping[visibility] || ''
        },

        formatCommunicationsVisibility(visibility) {
            const mapping = {
                [CommunicationAccessTypes.COMMUNICATIONS_ACCESS_ALL]: 'Everything',
                [CommunicationAccessTypes.COMMUNICATIONS_OWNED_ONLY]: 'Owned Only'
            }
            return mapping[visibility] || ''
        },

        async refresh() {
            try {
                this.loading_users = true
                if (!this.hasPermissionTo('list user')) {
                    return
                }

                const response = await axios.get('/api/v2/users', {
                    mode: 'no-cors'
                })

                this.setUsers(response.data)
                this.$message.success('Agents refreshed successfully')
            } catch (error) {
                this.$message.error('Failed to refresh users')
            } finally {
                this.loading_users = false
            }
        }
    }
}
</script>
